import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PracticeArea } from '../types/practiceArea';

interface UserProfile {
  id: string;
  email: string;
  displayName?: string;
  subscriptionTier: string;
  primary_practice_area: PracticeArea | null;
  practice_areas: PracticeArea[];
  // Drives the post-OAuth signup-wizard gate in App.tsx.
  // Without this field, the gate condition silently evaluates to false
  // (undefined === false) and brand-new users skip onboarding entirely.
  is_onboarded: boolean;
  // Admin-granted corporate suite access — 'none' | 'pending' | 'granted'.
  // Server-derived and read-only; the corporate wall enforces it regardless of
  // what the client believes. Used to show "awaiting approval" instead of a
  // launch button that would bounce. TEMPORARY pre-subscription beta gate.
  corporate_access?: 'none' | 'pending' | 'granted';
  // Staff permission, server-derived and read-only. 'support' lands on the
  // Support Desk shell only (see utils/appGate.ts). Missing = 'none'.
  staff_role?: 'none' | 'support';
  // Drives LegalGate in App.tsx. null/undefined means "never accepted" —
  // compared against GET /meta/legal-version's `version` on every load, so a
  // future ToS bump re-triggers the gate for everyone automatically.
  terms_accepted_at?: string | null;
  terms_version?: string | null;
}

interface AuthState {
  isAuthenticated: boolean;
  user: UserProfile | null;
  loading: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: true,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<UserProfile | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.loading = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
    },
  },
});

export const { setUser, setLoading, logout } = authSlice.actions;
export default authSlice.reducer;
